"use client";

import React from "react";
import { DraftListingData } from "../types";
import { ListingCardData, ListingTypeEnum } from "@/types/types";
import styles from "./live-preview.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import ListingCardPremium from "@/app/[locale]/(client)/search-old/[...slugs]/components/listing-cards/listing-card-premium";
import ListingCardLoading from "@/app/[locale]/(client)/search-old/[...slugs]/components/listing-cards/listing-card-loading";
import ListingCard from "@/app/[locale]/(client)/search-old/[...slugs]/components/listing-cards/listing-card";

export default function LivePreviewCard({
  data,
}: {
  data: DraftListingData | null;
  t: any;
}) {
  const { userData } = useUserAuthDataContext();
  if (!data) {
    return (
      <div className={styles.wrapper}>
        <ListingCardLoading activePlan={"prime"} />
      </div>
    );
  }

  let displayAddress;
  if (data.location_on_request) {
    // PRIVACY ON: Compose a generic location string safely (City, Region, Country)
    const parts = [];
    if (data.city?.en) parts.push(data.city.en);
    if (data.region?.en) parts.push(data.region.en);
    if (data.country?.en) parts.push(data.country.en);

    displayAddress =
      parts.length > 0 ? parts.join(", ") : "Approximate location saved.";
  } else {
    // PRIVACY OFF:

    // 2. Reconstruct the full exact address from raw components!
    // We cannot trust data.address.en here because it might be the stripped version from a previous save.
    let reconstructed = "";

    const city = data.city?.en || "";
    const region = data.region?.en || "";
    const country = data.country?.en || "";
    const parts = [data.native_street_line, city, region, country].filter(
      Boolean,
    );

    reconstructed =
      parts.length > 0
        ? parts.join(", ")
        : data.address?.en || "Fetching address details...";

    displayAddress =
      reconstructed || data.address?.en || "Fetching address details...";
  }

  // Map DraftData directly to ListingCardData
  const mockCardData: ListingCardData = {
    listing_id: data.listing_id || "preview-id",
    title: data.title || "Untitled Property",
    price: data.price || 0,
    purpose: data.purpose as any,
    type: data.type as any,
    number_of_bedrooms: data.number_of_bedrooms,
    number_of_bathrooms: data.number_of_bathrooms,
    number_of_rooms: data.number_of_rooms,
    interior_space: data.interior_space || 0,
    lot_size: data.lot_size || 0,

    // --- ADDED MISSING FIELD HERE ---
    map_location: data.map_location || { lat: 0, long: 0 },

    address: displayAddress || data.address?.en || "Location not set",

    photos: data.photos || {},

    listing_type: data.listing_type || ListingTypeEnum.ESSENTIAL,
    created_by: "preview-user",
    creator: {
      id: userData.sub || "",
      name: userData.complete_name,
      user_type: userData.user_type as any,
      photo_url: userData.user_photo,
      phone_number: "",
      has_whatsapp_active: false,
    },
    // Fill in required mocks for preview
    available_from: data.available_from || "",
    presentation_video_data: data.presentation_video || ({} as any),
    brochure: data.brochure || ({} as any),
    city: data.city?.en || "",
    status: "active",
    is_saved: false,
    version: 1,
    web_tour_url: data.web_tour_url || "",
    has_web_tour: !!data.web_tour_url,
    has_presentation_video: !!data.presentation_video,
    has_brochure: !!data.brochure,
    price_on_request: data.price_on_request || false,
    location_on_request: data.location_on_request || false,
    is_price_per_year: data.is_price_per_year || false,
    view: data.view || null,
    orientation: data.orientation || null,
    short_description: data.description || "",
  };

  const isPremiumTier =
    data.listing_type === ListingTypeEnum.PRIME ||
    data.listing_type === ListingTypeEnum.SPOTLIGHT;
  return (
    <div
      onClickCapture={(e) => {
        const target = e.target as HTMLElement;

        // If the user is clicking the Swiper Next/Back buttons, let it happen!
        if (target.closest("button")) {
          return;
        }

        // Otherwise, stop the click so the user isn't navigated away from the form
        e.preventDefault();
        e.stopPropagation();
      }}
      className={styles.wrapper}
    >
      {isPremiumTier ? (
        <ListingCardPremium
          data={mockCardData}
          isResizable={false}
          hasActions={false}
          hasRoundButtons={false}
        />
      ) : (
        <ListingCard
          data={mockCardData}
          isResizable={false}
          hasActions={false}
          hasRoundButtons={false}
        />
      )}
    </div>
  );
}
